import { c as _c } from "react/compiler-runtime";
import { FormLabel, FormHelperText, FormControl, Stack, Divider, Box, Select, MenuItem, Tooltip } from '@mui/material';
import { AccountState, type Account } from '@repo/api-gw-sdk';
import { useState } from 'react';
import { BulletItem } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/bulletItem';
import { NumericBulletSection } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/numericBulletSection';
import { FormTextField } from '@/components/form/formTextField';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import type { BackupVault } from '@/data/vaults/backupVault';
import { getDocsUrl } from '@/docs/docsUrls';
import { ByovInfrastructureRequirements } from './byovInfrastructureRequirements';
import { mrapAliasFieldValidation } from './byovPolicyUtils';
import { ByovSetupMethod } from './byovSetupMethod';
import { ByovSetupScriptPanel } from './byovSetupScriptPanel';
import { MrapCopyablePolicy } from './mrapCopyablePolicy';
import { VaultOwnBucketCopyableAllAccountsPolicy } from './vaultOwnBucketCopyablePolicy';
import { VaultOwnBucketEditKeyPolicyButton } from './vaultOwnBucketEditKeyPolicyButton';
import { BottomDialog } from '../dialogs/bottomDialog';
import { PanelWrapper } from '../panels/panelWrapper';
import { useBuckets } from '../restore/useBuckets';
import { SelectPlaceholder } from '../selectPlaceholder';
import { ReadMoreLink } from '../shared/readMoreLink';
export const SelectVaultBucketPanel = t0 => {
  const $ = _c(68);
  const {
    open,
    onClose,
    disabled,
    data,
    setData,
    vaultAccounts
  } = t0;
  const {
    byovEnabled: t1,
    EnableMrapForByov: t2
  } = useFeatureFlags();
  const byovEnabled = t1 === undefined ? false : t1;
  const enableMrapForByov = t2 === undefined ? false : t2;
  const [method, setMethod] = useState("console");
  const enableMrap = byovEnabled && enableMrapForByov;
  let t3;
  if ($[0] !== data.vaultAttributes.aws?.mrapAlias) {
    t3 = data.vaultAttributes.aws?.mrapAlias?.trim() ?? "";
    $[0] = data.vaultAttributes.aws?.mrapAlias;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const mrapAlias = t3;
  let t4;
  if ($[2] !== data.vaultAccountId || $[3] !== vaultAccounts) {
    let t5;
    if ($[5] !== data.vaultAccountId) {
      t5 = account => account.id === data.vaultAccountId;
      $[5] = data.vaultAccountId;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    t4 = vaultAccounts.find(t5);
    $[2] = data.vaultAccountId;
    $[3] = vaultAccounts;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const selectedVaultAccount = t4;
  let t5;
  let t6;
  if ($[7] !== onClose) {
    t5 = {
      title: "Select bucket",
      subtitle: "Select a bucket from your self-managed vault account. You\u2019re responsible for the vault\u2019s security, availability, and ensuring Eon has access to manage snapshots.",
      fullWidthSubtitle: false,
      onClose
    };
    t6 = {
      primaryButton: {
        text: "Done",
        "data-testid": "done-button",
        onClick: onClose
      },
      secondaryButton: {
        text: "Cancel",
        "data-testid": "cancel-button",
        onClick: onClose
      }
    };
    $[7] = onClose;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      padding: "40px min(100px,6vw)",
      width: "100%",
      height: "auto"
    };
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Divider sx={{
      borderStyle: "dashed"
    }} />;
    t9 = {
      gap: "40px"
    };
    $[11] = t8;
    $[12] = t9;
  } else {
    t8 = $[11];
    t9 = $[12];
  }
  let t10;
  if ($[13] !== method) {
    t10 = <ByovSetupMethod method={method} onChange={setMethod} />;
    $[13] = method;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      gap: "24px"
    };
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const t12 = data.vaultAccountId || "";
  let t13;
  if ($[16] !== data || $[17] !== setData) {
    t13 = vaultAccountId => {
      setData({
        ...data,
        vaultAccountId,
        vaultAttributes: {
          ...data.vaultAttributes,
          aws: undefined
        }
      });
    };
    $[16] = data;
    $[17] = setData;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== disabled || $[20] !== t12 || $[21] !== t13 || $[22] !== vaultAccounts) {
    t14 = <VaultAccountSelector disabled={disabled} vaultAccountId={t12} setAccountId={t13} vaultAccounts={vaultAccounts} />;
    $[19] = disabled;
    $[20] = t12;
    $[21] = t13;
    $[22] = vaultAccounts;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== selectedVaultAccount) {
    t15 = selectedVaultAccount || {} as Account;
    $[24] = selectedVaultAccount;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  const t16 = data.region || "";
  const t17 = data.vaultAttributes.aws?.bucketName || "";
  let t18;
  if ($[26] !== data || $[27] !== setData) {
    t18 = bucketName => {
      setData({
        ...data,
        vaultAttributes: {
          ...data.vaultAttributes,
          aws: {
            ...data.vaultAttributes.aws,
            bucketName
          }
        }
      });
    };
    $[26] = data;
    $[27] = setData;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== disabled || $[30] !== t15 || $[31] !== t16 || $[32] !== t17 || $[33] !== t18) {
    t19 = <BucketSelector account={t15} region={t16} disabled={disabled} bucketName={t17} setBucketName={t18} />;
    $[29] = disabled;
    $[30] = t15;
    $[31] = t16;
    $[32] = t17;
    $[33] = t18;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== t14 || $[36] !== t19) {
    t20 = <BulletItem leftContent={<Stack sx={t11}>{t14}{t19}</Stack>} />;
    $[35] = t14;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t10 || $[39] !== t20) {
    t21 = <NumericBulletSection bulletNumber={1} title="Select vault account and bucket" titleAction={t10}>{t20}</NumericBulletSection>;
    $[38] = t10;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== data.region || $[42] !== data.vaultAttributes.aws?.bucketName || $[43] !== method || $[44] !== selectedVaultAccount?.providerAccountId) {
    t22 = method === "script" ? <NumericBulletSection bulletNumber={2} title="Set up your bucket with AWS CLI"><ByovSetupScriptPanel bucketName={data.vaultAttributes.aws?.bucketName || ""} region={data.region} accountId={selectedVaultAccount?.providerAccountId || ""} /></NumericBulletSection> : <Stack sx={{
      gap: "40px"
    }} divider={<Divider sx={{
      borderStyle: "dashed"
    }} />}><NumericBulletSection bulletNumber={2} title="Create Lifecycle Rule in AWS"><ByovInfrastructureRequirements bucketName={data.vaultAttributes.aws?.bucketName || ""} region={data.region} section="lifecycle" /></NumericBulletSection><NumericBulletSection bulletNumber={3} title="Edit Bucket Versioning in AWS"><ByovInfrastructureRequirements bucketName={data.vaultAttributes.aws?.bucketName || ""} region={data.region} section="versioning" /></NumericBulletSection><NumericBulletSection bulletNumber={4} title="Edit Bucket Policy in AWS"><BulletItem title="Merge these statements into your bucket policy, then choose Save changes. Return here to finish the connection." leftContent={<VaultOwnBucketEditKeyPolicyButton region={data.region} bucketName={data.vaultAttributes.aws?.bucketName || ""} />} rightContent={<VaultOwnBucketCopyableAllAccountsPolicy bucketName={data.vaultAttributes.aws?.bucketName || ""} />} /></NumericBulletSection></Stack>;
    $[41] = data.region;
    $[42] = data.vaultAttributes.aws?.bucketName;
    $[43] = method;
    $[44] = selectedVaultAccount?.providerAccountId;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== data || $[47] !== enableMrap || $[48] !== method || $[49] !== setData) {
    t23 = enableMrap && <NumericBulletSection bulletNumber={method === "script" ? 3 : 5} title="Configure Multi-Region Access Point (optional)"><BulletItem title="If you need cross-region restore into isolated subnets, provide the alias of an S3 Multi-Region Access Point configured for this bucket." leftContent={<Stack sx={{
        gap: "8px"
      }}><FormTextField size="small" title="" placeholder="e.g. mfzwi23gnjvgw.mrap" validation={mrapAliasFieldValidation} value={data.vaultAttributes.aws?.mrapAlias ?? ""} onChange={e => {
          setData({
            ...data,
            vaultAttributes: {
              ...data.vaultAttributes,
              aws: {
                ...data.vaultAttributes.aws,
                mrapAlias: e.target.value
              }
            }
          });
        }} dataTestId="vault-mrap-alias-input" /><ReadMoreLink href={getDocsUrl("awsRestoreVpcEndpoints")} /></Stack>} /></NumericBulletSection>;
    $[46] = data;
    $[47] = enableMrap;
    $[48] = method;
    $[49] = setData;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== enableMrap || $[52] !== method || $[53] !== mrapAlias || $[54] !== selectedVaultAccount) {
    t24 = enableMrap && mrapAlias && <NumericBulletSection bulletNumber={method === "script" ? 4 : 6} title="Add MRAP policy in AWS"><BulletItem title="Review and add this policy on your Multi-Region Access Point. This allows Eon restore nodes to read objects through the MRAP." leftContent={<MrapCopyablePolicy mrapAlias={mrapAlias} vaultAccount={selectedVaultAccount} />} /></NumericBulletSection>;
    $[51] = enableMrap;
    $[52] = method;
    $[53] = mrapAlias;
    $[54] = selectedVaultAccount;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== t21 || $[57] !== t22 || $[58] !== t23 || $[59] !== t24) {
    t25 = <Box sx={t7}><Stack divider={t8} sx={t9}>{t21}{t22}{t23}{t24}</Stack></Box>;
    $[56] = t21;
    $[57] = t22;
    $[58] = t23;
    $[59] = t24;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  let t26;
  if ($[61] !== t25 || $[62] !== t5 || $[63] !== t6) {
    t26 = <PanelWrapper header={t5} footer={t6}>{t25}</PanelWrapper>;
    $[61] = t25;
    $[62] = t5;
    $[63] = t6;
    $[64] = t26;
  } else {
    t26 = $[64];
  }
  let t27;
  if ($[65] !== open || $[66] !== t26) {
    t27 = <BottomDialog open={open}>{t26}</BottomDialog>;
    $[65] = open;
    $[66] = t26;
    $[67] = t27;
  } else {
    t27 = $[67];
  }
  return t27;
};
const BucketSelector = t0 => {
  const $ = _c(18);
  const {
    disabled,
    bucketName,
    setBucketName,
    account,
    region
  } = t0;
  const {
    buckets,
    isLoading,
    missingListPermissions,
    listFailed
  } = useBuckets(account, region);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <FormLabel>Bucket</FormLabel>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = disabled || isLoading || listFailed;
  const t3 = bucketName || "";
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      "data-testid": "vault-bucket-name"
    };
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== setBucketName) {
    t5 = event => {
      setBucketName(event.target.value);
    };
    $[2] = setBucketName;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== buckets) {
    t6 = buckets.map(_temp2);
    $[4] = buckets;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t2 || $[7] !== t3 || $[8] !== t5 || $[9] !== t6) {
    t7 = <Select disabled={t2} size="small" value={t3} inputProps={t4} displayEmpty={true} renderValue={_temp} onChange={t5}>{t6}</Select>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== listFailed || $[12] !== missingListPermissions) {
    t8 = listFailed && <FormHelperText>{missingListPermissions ? "AWS denied listing buckets. Make sure the vault verification role allows s3:ListAllMyBuckets and s3:GetBucketLocation." : "Can't load buckets from the selected vault account. If the problem persists, contact support."}</FormHelperText>;
    $[11] = listFailed;
    $[12] = missingListPermissions;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== listFailed || $[15] !== t7 || $[16] !== t8) {
    t9 = <FormControl size="small" className="w-full px-[0px]" error={listFailed}>{t1}{t7}{t8}</FormControl>;
    $[14] = listFailed;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  return t9;
};
const VaultAccountSelector = t0 => {
  const $ = _c(15);
  const {
    disabled,
    vaultAccountId,
    setAccountId,
    vaultAccounts
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <FormLabel>Vault account</FormLabel>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = vaultAccountId || "";
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      "data-testid": "vault-bucket-name"
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== vaultAccounts) {
    t4 = value => {
      if (!value) {
        return <SelectPlaceholder />;
      }
      return vaultAccounts.find(account => account.id === value)?.name || "";
    };
    $[2] = vaultAccounts;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== setAccountId || $[5] !== vaultAccountId) {
    t5 = event => {
      if (event.target.value === vaultAccountId || !event.target.value) {
        return;
      }
      setAccountId(event.target.value);
    };
    $[4] = setAccountId;
    $[5] = vaultAccountId;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== vaultAccounts) {
    t6 = vaultAccounts.map(_temp3);
    $[7] = vaultAccounts;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== disabled || $[10] !== t2 || $[11] !== t4 || $[12] !== t5 || $[13] !== t6) {
    t7 = <FormControl size="small" className="w-full px-[0px]">{t1}<Select disabled={disabled} size="small" value={t2} inputProps={t3} displayEmpty={true} renderValue={t4} onChange={t5}>{t6}</Select></FormControl>;
    $[9] = disabled;
    $[10] = t2;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
};
function _temp(value) {
  if (!value) {
    return <SelectPlaceholder />;
  }
  return value;
}
function _temp2(bucket) {
  return <MenuItem key={bucket.bucketName} value={bucket.bucketName}>{bucket.bucketName}</MenuItem>;
}
function _temp3(account_0) {
  return <Tooltip key={account_0.id} title={account_0.state === AccountState.ACCOUNT_STATE_DISCONNECTED ? "Disconnected" : ""} placement="right"><span><MenuItem value={account_0.id} disabled={account_0.state === AccountState.ACCOUNT_STATE_DISCONNECTED}>{account_0.name}</MenuItem></span></Tooltip>;
}